import { useEffect, useState } from 'react';

/**
 * Hell oder dunkel -- und "wie das Geraet".
 *
 * Wie die Tastenbelegung gehoert das Thema dem Geraet, nicht dem Konto: wer
 * am Abend am Telefon schneidet, will es dunkel, und morgens am hellen
 * Schreibtisch vielleicht nicht. Deshalb im Browser gespeichert.
 *
 * Die Buehne bleibt in beiden Themen fast schwarz. Das ist kein Versehen: auf
 * einem hellen Grund beurteilt niemand Farbe, und deshalb ist die Leinwand in
 * jedem Schnittprogramm das Dunkelste auf dem Schirm.
 */
export type Thema = 'geraet' | 'hell' | 'dunkel';

export const THEMEN: { id: Thema; label: string }[] = [
  { id: 'geraet', label: 'Wie das Gerät' },
  { id: 'hell', label: 'Hell' },
  { id: 'dunkel', label: 'Dunkel' },
];

const SCHLUESSEL = 'schnitt.thema';

export function gelesenesThema(): Thema {
  try {
    const wert = localStorage.getItem(SCHLUESSEL);
    if (wert === 'hell' || wert === 'dunkel' || wert === 'geraet') return wert;
  } catch {
    /* Ein Browser ohne Speicher ist kein Grund, nicht zu arbeiten. */
  }
  return 'geraet';
}

/** Was daraus wirklich wird: „geraet“ fragt das Betriebssystem. */
export function wirksam(thema: Thema): 'hell' | 'dunkel' {
  if (thema !== 'geraet') return thema;
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'hell' : 'dunkel';
  } catch {
    return 'dunkel';
  }
}

/**
 * Setzt das Thema auf das Wurzelelement.
 *
 * `data-theme` steht dort und nicht als Klasse, weil das Stilblatt danach
 * greift; die Farbe der Browserleiste zieht mit, sonst blitzt oben ein
 * dunkler Balken ueber einer hellen Seite. Und `color-scheme` sagt dem
 * Browser, wie er Rollbalken und Eingabefelder zeichnen soll -- ohne das
 * bleiben sie dunkel und stehen als graue Kaesten in der hellen Seite.
 */
export function anwenden(thema: Thema): void {
  const hell = wirksam(thema) === 'hell';
  const wurzel = document.documentElement;
  wurzel.dataset.theme = hell ? 'light' : 'dark';
  wurzel.style.colorScheme = hell ? 'light' : 'dark';
  const marke = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (marke) marke.content = hell ? '#f7f4f0' : '#121212';
}

const horcher = new Set<(thema: Thema) => void>();

export function setThema(thema: Thema): void {
  try {
    localStorage.setItem(SCHLUESSEL, thema);
  } catch {
    /* siehe oben */
  }
  anwenden(thema);
  for (const h of horcher) h(thema);
}

export function useThema(): Thema {
  const [thema, setzen] = useState<Thema>(gelesenesThema);
  useEffect(() => {
    horcher.add(setzen);
    // Steht es auf „wie das Gerät“, folgt es dem Gerät auch, während die App
    // offen ist -- iOS schaltet abends von selbst um.
    const medium = window.matchMedia('(prefers-color-scheme: light)');
    const folgen = () => anwenden(gelesenesThema());
    medium.addEventListener('change', folgen);
    return () => {
      horcher.delete(setzen);
      medium.removeEventListener('change', folgen);
    };
  }, []);
  return thema;
}
